import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Loader2, BookOpen, RotateCcw, Coffee, BookMarked, Check, ArrowRight } from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatInterface() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { loadConversation(); }, []);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const loadConversation = async () => {
    setInitializing(true);
    try {
      const res = await axios.get(`${API}/conversation`);
      setMessages(res.data.messages || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load conversation");
    } finally {
      setInitializing(false);
    }
  };

  const clearConversation = async () => {
    setClearing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${API}/conversation/clear`);
      setMessages(res.data.messages || []);
      toast.success("Conversation cleared — fresh start!");
    } catch (e) {
      toast.error("Failed to clear conversation");
    } finally {
      setClearing(false);
    }
  };

  const sendMessage = async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMsg, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, { message: userMsg });
      setMessages((prev) => [...prev, res.data.assistant_message]);
    } catch (e) {
      toast.error("Failed to get response. Please try again.");
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const syncToBible = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${API}/chat/sync-to-bible`);
      setSyncResult(res.data);
      if (res.data.status === "updated") {
        toast.success(res.data.message);
      } else {
        toast.info(res.data.message);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to sync insights";
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleVoiceTranscript = (text) => { sendMessage(text); };

  if (initializing) {
    return (
      <div className="flex h-screen items-center justify-center bg-stone-50/50" data-testid="chat-loading">
        <div className="text-center">
          <div className="w-16 h-16 bg-stone-900 rounded-2xl flex items-center justify-center mb-6 mx-auto">
            <Loader2 className="w-8 h-8 text-stone-50 animate-spin" />
          </div>
          <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Loading your conversation...
          </h3>
          <p className="text-stone-500 text-sm">Connecting with your Coffee Consultant</p>
        </div>
      </div>
    );
  }

  const userMessageCount = messages.filter(m => m.role === "user").length;

  return (
    <div className="flex h-screen flex-col bg-stone-50/50" data-testid="chat-interface">
      {/* Chat Header */}
      <div className="glass-header px-6 py-4 flex items-center justify-between border-b border-stone-200/60">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-stone-900 rounded-xl flex items-center justify-center">
            <Coffee className="w-5 h-5 text-stone-50" />
          </div>
          <div>
            <h2 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              AI Consultant
            </h2>
            <p className="text-xs text-stone-500">Building your Business Bible through conversation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {userMessageCount >= 2 && (
            <Button
              data-testid="sync-to-bible-btn"
              variant="outline"
              size="sm"
              className="border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800 text-sm font-medium"
              onClick={syncToBible}
              disabled={syncing}
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BookMarked className="w-4 h-4 mr-2" />
              )}
              {syncing ? "Syncing..." : "Sync to Bible"}
            </Button>
          )}
          {messages.length > 2 && (
            <Button
              data-testid="generate-playbook-btn"
              variant="outline"
              size="sm"
              className="border-stone-200 hover:bg-stone-50 text-sm font-medium"
              onClick={async () => {
                try {
                  toast.info("Generating playbook... This may take a moment.");
                  const res = await axios.post(`${API}/playbooks/generate`, { title: "Coffee Shop Playbook" });
                  toast.success("Playbook generated!");
                  navigate(`/playbooks/${res.data.id}`);
                } catch (e) {
                  toast.error("Failed to generate playbook");
                }
              }}
            >
              <BookOpen className="w-4 h-4 mr-2" /> Generate Playbook
            </Button>
          )}
          <Button
            data-testid="clear-conversation-btn"
            variant="outline"
            size="sm"
            onClick={clearConversation}
            disabled={clearing || messages.length === 0}
            className="border-stone-200 hover:bg-stone-50 text-sm font-medium"
          >
            {clearing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
            {clearing ? "Clearing..." : "Start Over"}
          </Button>
        </div>
      </div>

      {/* Sync Result Banner */}
      {syncResult && syncResult.status === "updated" && (
        <div className="mx-6 mt-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl animate-slide-up" data-testid="sync-result-banner">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-900">{syncResult.message}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {syncResult.updated_fields.map((f, i) => (
                    <span key={i} className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-emerald-700 hover:bg-emerald-100 text-xs"
              onClick={() => navigate("/playbooks/business-bible")}
              data-testid="view-bible-btn"
            >
              View Bible <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {syncResult && syncResult.status === "no_updates" && (
        <div className="mx-6 mt-3 p-3 bg-stone-100 border border-stone-200 rounded-xl animate-slide-up" data-testid="sync-no-updates">
          <p className="text-sm text-stone-600">{syncResult.message}</p>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 px-6 py-4">
        <div className="max-w-3xl mx-auto space-y-4 pb-4">
          {messages.length === 0 && (
            <div className="text-center py-12 animate-fade-in">
              <div className="flex items-center gap-1.5 justify-center mb-3">
                <div className="typing-dot w-2 h-2 bg-stone-400 rounded-full" />
                <div className="typing-dot w-2 h-2 bg-stone-400 rounded-full" />
                <div className="typing-dot w-2 h-2 bg-stone-400 rounded-full" />
              </div>
              <p className="text-stone-500">Your consultant is preparing questions...</p>
            </div>
          )}
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`animate-slide-up flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              style={{ animationDelay: `${Math.min(idx * 0.05, 0.3)}s` }}
            >
              <div
                data-testid={`chat-message-${idx}`}
                className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${
                  msg.role === "user"
                    ? "bg-stone-900 text-stone-50"
                    : "bg-white border border-stone-200/60 shadow-sm text-stone-800"
                }`}
              >
                {msg.role === "assistant" ? (
                  <div className="chat-markdown text-sm leading-relaxed">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm leading-relaxed">{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start animate-fade-in">
              <div className="bg-white border border-stone-200/60 rounded-2xl px-5 py-4 shadow-sm">
                <div className="flex items-center gap-1.5">
                  <div className="typing-dot w-2 h-2 bg-stone-400 rounded-full" />
                  <div className="typing-dot w-2 h-2 bg-stone-400 rounded-full" />
                  <div className="typing-dot w-2 h-2 bg-stone-400 rounded-full" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t border-stone-200/60 bg-white px-6 py-4">
        <div className="max-w-3xl mx-auto flex gap-3 items-end">
          <textarea
            ref={inputRef}
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your answer..."
            rows={1}
            className="flex-1 bg-stone-50 border border-stone-200 text-stone-900 placeholder:text-stone-400 focus:border-stone-400 focus:ring-2 focus:ring-stone-900/5 transition-all duration-200 rounded-xl px-4 py-3 text-sm resize-none shadow-sm"
            style={{ minHeight: "44px", maxHeight: "120px" }}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
            }}
          />
          <VoiceRecorder onTranscript={handleVoiceTranscript} disabled={loading} />
          <Button
            data-testid="send-message-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all duration-200 rounded-xl px-4 self-end"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
